import { useQuery } from "@tanstack/react-query";
import { dashApi } from "@/lib/dashApi";
import { useFilters } from "@/lib/filters";
import { fmtCurrency, fmtRelative } from "@/lib/format";
import { num, rows, type LabelValue, type NameValue } from "@/lib/kpi";
import {
  BarList,
  ErrorState,
  GapList,
  KpiCard,
  Leaderboard,
  LoadingState,
  PageHeader,
  Panel,
  SourceBadge,
  StatusPill,
} from "@/components/dash/ui";
import SalesOverview from "@/components/dash/SalesPanels";

type RecentOrder = { reference: string; total: number; date: string | null; customer: string; status: string };

export default function FinancePage() {
  const { range } = useFilters();
  const odoo = useQuery({ queryKey: ["src", "odoo", range], queryFn: () => dashApi.source("odoo", range) });
  const presta = useQuery({ queryKey: ["src", "prestashop", range], queryFn: () => dashApi.source("prestashop", range) });
  const salesQ = useQuery({ queryKey: ["sales", range], queryFn: () => dashApi.sales(range) });
  const ok = odoo.data?.kpis;
  const pk = presta.data?.kpis;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Sales & Finance"
        description="Authoritative business sales (Odoo + PrestaShop), with repairs shown separately."
        right={salesQ.data?.window && <div className="text-right text-xs text-slate-500"><div className="text-slate-300">{salesQ.data.window.from} → {salesQ.data.window.to}</div><div>{salesQ.data.window.mode === "live" ? "Live query" : "From snapshots"}</div></div>}
      />

      {salesQ.isLoading ? <LoadingState /> : salesQ.isError ? <ErrorState error={salesQ.error} /> : salesQ.data ? (
        <SalesOverview sales={salesQ.data} />
      ) : null}

      <h2 className="pt-2 text-sm font-semibold uppercase tracking-wide text-slate-400">Source detail</h2>
      {odoo.isError ? <ErrorState error={odoo.error} /> : null}

      <Panel
        title={<span className="flex items-center gap-2"><SourceBadge code="odoo" /> Odoo</span>}
        right={odoo.data && <span className="flex items-center gap-2"><StatusPill status={odoo.data.status} /><span className="text-xs text-slate-500">{fmtRelative(odoo.data.captured_at)}</span></span>}
      >
        {odoo.isLoading ? <LoadingState /> : (
          <div className="space-y-5">
            <div className="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-5">
              <KpiCard label="Sales" value={num(ok, "sales")} unit="EUR" delta={{ pct: pctDelta(num(ok, "sales"), num(ok, "prev_sales")) }} />
              <KpiCard label="Purchases" value={num(ok, "purchases")} unit="EUR" />
              <KpiCard label="Invoiced" value={num(ok, "invoiced")} unit="EUR" />
              <KpiCard label="Unpaid" value={num(ok, "unpaid_total")} unit="EUR" tone="danger" />
              <KpiCard label="Expenses" value={num(ok, "expenses")} unit="EUR" />
            </div>
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
              <Panel title="Top salespeople"><Leaderboard rows={rows<NameValue>(ok, "top_salespeople")} unit="EUR" /></Panel>
              <Panel title="Top customers"><Leaderboard rows={rows<NameValue>(ok, "top_customers")} unit="EUR" /></Panel>
              <Panel title="By company"><Leaderboard rows={rows<NameValue>(ok, "company_split")} unit="EUR" /></Panel>
            </div>
            {odoo.data && <GapList gaps={odoo.data.gaps} />}
          </div>
        )}
      </Panel>

      <Panel
        title={<span className="flex items-center gap-2"><SourceBadge code="prestashop" /> PrestaShop</span>}
        right={presta.data && <span className="flex items-center gap-2"><StatusPill status={presta.data.status} /><span className="text-xs text-slate-500">{fmtRelative(presta.data.captured_at)}</span></span>}
      >
        {presta.isLoading ? <LoadingState /> : (
          <div className="space-y-5">
            <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
              <KpiCard label="Sales" value={num(pk, "sales")} unit="EUR" delta={{ pct: pctDelta(num(pk, "sales"), num(pk, "prev_sales")) }} />
              <KpiCard label="Orders" value={num(pk, "orders")} unit="count" />
              <KpiCard label="Avg order value" value={num(pk, "aov")} unit="EUR" />
              <KpiCard label="Prev sales" value={num(pk, "prev_sales")} unit="EUR" />
            </div>
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
              <Panel title="Order status mix"><BarList rows={rows<LabelValue>(pk, "status_distribution")} unit="count" /></Panel>
              <Panel title="Top customers"><Leaderboard rows={rows<NameValue>(pk, "top_customers")} unit="EUR" /></Panel>
              <Panel title="Top products"><Leaderboard rows={rows<NameValue>(pk, "top_products")} unit="EUR" /></Panel>
            </div>
            <Panel title="Recent orders">
              <RecentOrders rows={rows<RecentOrder>(pk, "recent_orders")} />
            </Panel>
            {presta.data && <GapList gaps={presta.data.gaps} />}
          </div>
        )}
      </Panel>
    </div>
  );
}

function pctDelta(cur: number | null, prev: number | null): number | null {
  if (cur === null || prev === null || prev === 0) return null;
  return ((cur - prev) / prev) * 100;
}

function RecentOrders({ rows }: { rows: RecentOrder[] }) {
  if (!rows.length) return <p className="py-4 text-center text-sm text-slate-500">No recent orders.</p>;
  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="text-left text-[11px] uppercase tracking-wide text-slate-500">
          <th className="pb-2 font-medium">Reference</th>
          <th className="pb-2 font-medium">Customer</th>
          <th className="pb-2 font-medium">Status</th>
          <th className="pb-2 text-right font-medium">Total</th>
          <th className="pb-2 text-right font-medium">Date</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-ink-500/40">
        {rows.map((r, i) => (
          <tr key={i} className="text-slate-300">
            <td className="py-2 font-mono text-xs">{r.reference}</td>
            <td className="py-2">{r.customer}</td>
            <td className="py-2 text-slate-400">{r.status}</td>
            <td className="py-2 text-right tabular-nums">{fmtCurrency(r.total)}</td>
            <td className="py-2 text-right text-xs text-slate-500">{fmtRelative(r.date)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
